import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { APIURL } from '../api/config';
import {
  RefreshLimitPauseAlert,
  ExamPausedOnRefreshLimitPayload,
  RefreshLimitPauseResolvedPayload,
  getRefreshLimitPauseAlertKey,
  getRefreshLimitPauseLocalStorageKey,
} from '../types/refreshLimitPause.types';
import { playNotificationSound } from '../utils/playNotificationSound';

// ─── localStorage helpers ────────────────────────────────────────────────────
// Persisted so a recruiter refreshing the portal, or a second recruiter's PC, still sees a
// pause that was announced while they weren't looking.

function lsPersist(slotId: string, payload: RefreshLimitPauseAlert): void {
  try {
    localStorage.setItem(
      getRefreshLimitPauseLocalStorageKey(slotId, payload.candidateId),
      JSON.stringify(payload)
    );
  } catch (err) {
    console.warn('[RefreshLimitPause] localStorage.setItem failed:', err);
  }
}

function lsRemove(slotId: string, candidateId: string): void {
  try {
    localStorage.removeItem(getRefreshLimitPauseLocalStorageKey(slotId, candidateId));
  } catch (err) {
    console.warn('[RefreshLimitPause] localStorage.removeItem failed:', err);
  }
}

function lsRestoreForSlot(slotId: string): Map<string, RefreshLimitPauseAlert> {
  const result = new Map<string, RefreshLimitPauseAlert>();
  try {
    const prefix = `refreshLimitPause_${slotId}_`;
    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i);
      if (!lsKey?.startsWith(prefix)) continue;
      const raw = localStorage.getItem(lsKey);
      if (!raw) continue;
      const alert: RefreshLimitPauseAlert = JSON.parse(raw);
      result.set(getRefreshLimitPauseAlertKey(alert.candidateId, alert.slotId), alert);
    }
  } catch (err) {
    console.warn('[RefreshLimitPause] localStorage restore failed:', err);
  }
  return result;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Recruiter-side alert feed for candidates halted on the deliberate-reload limit.
 *
 *  - On mount: restore pending alerts from localStorage (survives a portal refresh)
 *  - ExamPausedOnRefreshLimit  → add alert + persist + play sound (once per candidate)
 *  - RefreshLimitPauseResolved → drop the alert on EVERY connected PC
 */
export function useRefreshLimitPauseAlerts(slotId: string | undefined) {
  const [activePauseAlerts, setActivePauseAlerts] = useState<Map<string, RefreshLimitPauseAlert>>(
    new Map()
  );
  // Own Set per hook instance, so a pause alert can never suppress the window-reset sound for
  // the same candidate (their alert keys would otherwise be identical).
  const soundPlayedKeysRef = useRef<Set<string>>(new Set());
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  useEffect(() => {
    if (!slotId) return;
    const stored = lsRestoreForSlot(slotId);
    if (stored.size > 0) {
      setActivePauseAlerts(stored);
    }
  }, [slotId]);

  // Own connection, matching useSlotWindowResetAlerts. Deliberately not shared with that hook:
  // consolidating them would mean rewriting the lifecycle of a live feature to save one socket
  // on a low-traffic page, and a teardown bug there would only surface after a reconnect.
  useEffect(() => {
    if (!slotId) return;

    const hubBaseUrl = APIURL.replace(/\/api$/, '');
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubBaseUrl}/hubs/assessment`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    const handlePaused = (payload: ExamPausedOnRefreshLimitPayload) => {
      const key = getRefreshLimitPauseAlertKey(payload.candidateId, payload.slotId);

      const alert: RefreshLimitPauseAlert = {
        candidateId: payload.candidateId,
        slotId: payload.slotId,
        candidateName: payload.candidateName,
        assessmentName: payload.assessmentName,
        refreshCount: payload.refreshCount,
        pausedAt: payload.pausedAt,
      };

      lsPersist(payload.slotId, alert);

      setActivePauseAlerts((prev) => {
        const next = new Map(prev);
        next.set(key, alert);
        return next;
      });

      if (!soundPlayedKeysRef.current.has(key)) {
        playNotificationSound();
        soundPlayedKeysRef.current.add(key);
      }
    };

    const handleResolved = (payload: RefreshLimitPauseResolvedPayload) => {
      clearAlert(payload.candidateId, payload.slotId);
    };

    function clearAlert(candidateId: string, alertSlotId: string) {
      const key = getRefreshLimitPauseAlertKey(candidateId, alertSlotId);
      lsRemove(alertSlotId, candidateId);
      setActivePauseAlerts((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      soundPlayedKeysRef.current.delete(key);
    }

    connection.on('ExamPausedOnRefreshLimit', handlePaused);
    connection.on('RefreshLimitPauseResolved', handleResolved);

    const joinSlot = () =>
      connection
        .invoke('JoinAdminAssessmentSlot', { assessmentSlotId: slotId })
        .catch((err) =>
          console.error('[RefreshLimitPause] JoinAdminAssessmentSlot failed:', err)
        );

    connection
      .start()
      .then(() => {
        joinSlot();
      })
      .catch((err) => {
        console.error('[RefreshLimitPause] Connection failed:', err);
      });

    // A reconnect gets a new connection id and group membership is per-connection, so the slot
    // group must be re-joined or later pause broadcasts are silently missed.
    connection.onreconnected(() => joinSlot());

    connectionRef.current = connection;

    return () => {
      connection.off('ExamPausedOnRefreshLimit', handlePaused);
      connection.off('RefreshLimitPauseResolved', handleResolved);
      connection.stop().catch(() => undefined);
      connectionRef.current = null;
    };
  }, [slotId]);

  /**
   * Broadcasts that this pause has been actioned so every recruiter PC drops the badge at once.
   * Falls back to local-only cleanup when the hub is unreachable.
   */
  const broadcastPauseResolved = useCallback(
    async (candidateId: string, alertSlotId: string) => {
      const conn = connectionRef.current;
      if (conn?.state === signalR.HubConnectionState.Connected) {
        try {
          await conn.invoke('BroadcastRefreshLimitPauseResolved', {
            candidateId,
            slotId: alertSlotId,
          });
          return;
        } catch (err) {
          console.error('[RefreshLimitPause] BroadcastRefreshLimitPauseResolved failed:', err);
        }
      }
      lsRemove(alertSlotId, candidateId);
      setActivePauseAlerts((prev) => {
        const next = new Map(prev);
        next.delete(getRefreshLimitPauseAlertKey(candidateId, alertSlotId));
        return next;
      });
      soundPlayedKeysRef.current.delete(getRefreshLimitPauseAlertKey(candidateId, alertSlotId));
    },
    []
  );

  const getPauseAlertForCandidate = useCallback(
    (candidateId: string): RefreshLimitPauseAlert | undefined => {
      if (!slotId) return undefined;
      return activePauseAlerts.get(getRefreshLimitPauseAlertKey(candidateId, slotId));
    },
    [activePauseAlerts, slotId]
  );

  return {
    activePauseAlerts,
    getPauseAlertForCandidate,
    broadcastPauseResolved,
  };
}
